import AddIcon from "@mui/icons-material/Add";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import {
  useCreateKnowledgeBaseArticleMutation,
  type KnowledgeBaseCategory,
} from "knowledge-base/_api/knowledge-base-api";
import type { KnowledgeBaseLibrary } from "knowledge-base/_schemas/knowledge-base-article-schema";
import { buildIndexTree, type CategoryNode } from "knowledge-base/_views/knowledge-base-index-tree";
import { useKnowledgeBaseLocations } from "knowledge-base/knowledge-base-locations";
import { useState } from "react";
import { useNavigate } from "react-router";

const UNFILED = "";

type Props = Readonly<{
  open: boolean;
  library: KnowledgeBaseLibrary;
  /** The categories of the knowledge base being written, for the "file it under" choice. */
  categories: readonly KnowledgeBaseCategory[];
  onClose: () => void;
}>;

/**
 * Starts a new article: a title, and where it goes.
 *
 * Only those two, because everything else about an article is better decided with the text in front
 * of you. The article is created as a draft and the editor opens on it, which is also what makes
 * this safe to use on a knowledge base people are reading — nothing appears in anyone's contents
 * until the author publishes it from there.
 */
export default function ArticleCreatorDialog({ open, library, categories, onClose }: Props) {
  const navigate = useNavigate();
  const { articleEditorPath } = useKnowledgeBaseLocations();

  const [title, setTitle] = useState("");
  const [categoryId, setCategoryId] = useState<string>(UNFILED);
  const [failure, setFailure] = useState<string | null>(null);

  const [createArticle, { isLoading: creating }] = useCreateKnowledgeBaseArticleMutation();

  const canCreate = title.trim().length > 0 && !creating;

  const create = async () => {
    setFailure(null);

    const outcome = await createArticle({
      title: title.trim(),
      // Empty on purpose: the editor is where the article gets written, and an article that opened
      // pre-filled with placeholder prose would ship that prose the first time someone forgot to
      // replace it.
      body: "",
      library,
      categoryId: categoryId === UNFILED ? undefined : categoryId,
      publish: false,
    });

    if ("error" in outcome) {
      setFailure(messageFor(outcome.error));
      return;
    }

    onClose();
    navigate(articleEditorPath(outcome.data.slug));
  };

  return (
    <Dialog fullWidth maxWidth="sm" onClose={creating ? undefined : onClose} open={open}>
      <DialogTitle>New article</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {failure && <Alert severity="error">{failure}</Alert>}

          <TextField
            fullWidth
            helperText="This also becomes the article's address, so it is worth saying what the page is about."
            label="Title"
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Validating an invoice"
            value={title}
          />

          <TextField
            fullWidth
            helperText="You can move it later by dragging it in the contents."
            label="File it under"
            onChange={(event) => setCategoryId(event.target.value)}
            select
            // "Not filed" is a real choice rather than the absence of one, and a select left on an
            // empty value shows a blank box instead of saying so.
            slotProps={{ inputLabel: { shrink: true }, select: { displayEmpty: true } }}
            value={categoryId}
          >
            <MenuItem value={UNFILED}>
              <em>Not filed</em>
            </MenuItem>
            {categoryChoices(categories).map((choice) => (
              <MenuItem key={choice.id} value={choice.id}>
                {`${"— ".repeat(choice.depth)}${choice.name}`}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button disabled={creating} onClick={onClose}>
          Cancel
        </Button>
        <Button
          disabled={!canCreate}
          onClick={() => void create()}
          startIcon={<AddIcon />}
          variant="contained"
        >
          {creating ? "Creating…" : "Create and write it"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export type CategoryChoice = Readonly<{ id: string; name: string; depth: number }>;

/**
 * The categories as a flat list, parents before their children, each knowing how deep it sits.
 *
 * Built from the same tree the contents are drawn from, so the order in this menu is the order on
 * screen. A select cannot nest, so depth becomes indentation rather than structure.
 */
export function categoryChoices(categories: readonly KnowledgeBaseCategory[]): CategoryChoice[] {
  const flatten = (nodes: readonly CategoryNode[], depth: number): CategoryChoice[] =>
    nodes.flatMap((node) => [
      { id: node.id, name: node.name, depth },
      ...flatten(node.children, depth + 1),
    ]);

  return flatten(buildIndexTree(categories, []).categories, 0);
}

/**
 * The server's own words where it gave any, and a plain sentence where it did not.
 *
 * Its refusals here are the useful kind — "'new' is reserved", "this knowledge base already has an
 * article at that address" — and each one names the thing the author has to change.
 */
function messageFor(error: unknown): string {
  if (error && typeof error === "object" && "data" in error) {
    const data = (error as { data: unknown }).data;
    if (typeof data === "string" && data.trim().length > 0) return data;
  }

  return "That article could not be created. Please try again.";
}
